/**
 * Shared preview content engine (2026-08-02): source resolution, content fetching, and
 * per-kind renderers used by BOTH the modal overlay (FilePreviewOverlay) and the
 * floating multitask window (FloatingPreviewWindow) — one implementation, two shells.
 *
 * Content loading is two-stage (2026-08-02 production fix): direct fetch() of the
 * attachment URL first, and when that fails — signed GCS URLs serve no CORS headers —
 * an authenticated fall-back to the prest0nResolveAttachmentContent endpoint, which
 * reads the object server-side and returns the bytes inline. A preview only degrades
 * to the download card when BOTH routes are exhausted.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { resolvePreviewKind, previewKindLabel, PreviewKind } from './previewKind';
import { htmlLooksIncomplete, htmlNeedsScripts, injectRuntimeGuard } from './sceneRuntimeGuard';
import {
  canResolveServerSide,
  resolveAttachmentContent,
  type AttachmentResolveContext,
} from './resolveAttachment';
import { downloadAttachmentAsFile, saveBlobAs } from './downloadAttachment';
import type { SerializedAttachment } from '../GlobalChatSystem/types';

export const ToolButton = styled.button`
  background: rgba(255, 255, 255, 0.07); color: #dbe4f2;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px; padding: 6px 10px;
  font-size: 12px; cursor: pointer; flex: 0 0 auto;
  &:hover { background: rgba(255, 255, 255, 0.14); }

  /* Touch: the 27px desktop pill is under every platform's target floor. */
  @media (pointer: coarse) {
    padding: 10px 14px;
    font-size: 13px;
    min-height: 40px;
  }
`;

export const FullFrame = styled.iframe`
  border: 0; width: 100%; height: 100%; background: #fff;
`;

const TextView = styled.pre<{ $wrap: boolean }>`
  width: 100%; height: 100%; margin: 0; padding: 16px;
  overflow: auto; font-size: 12.5px; line-height: 1.5;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #dbe4f2; background: #0a0f1a;
  white-space: ${(p) => (p.$wrap ? 'pre-wrap' : 'pre')};
  word-break: ${(p) => (p.$wrap ? 'break-word' : 'normal')};
`;

const CenterCard = styled.div`
  text-align: center; padding: 32px;
  /* Viewport-bounded (mobile UX 2026-08-14): a flat 420px cap overflowed a
     390px phone before padding was even counted. */
  max-width: min(420px, calc(100vw - 48px));
  display: flex; flex-direction: column; gap: 12px; align-items: center;
  color: #b9c4d6; font-size: 14px; margin: auto;
`;

/* R13 (2026-08-13 truncated-deliverable incident): shell for an HTML file
 * that opens like a full document but never reaches </html> — the cut-
 * generation signature that used to render as a silent black rectangle. */
const FrameColumn = styled.div`
  width: 100%; height: 100%; display: flex; flex-direction: column; min-height: 0;
`;

const IncompleteBanner = styled.div`
  flex: 0 0 auto; padding: 8px 14px; font-size: 12.5px; line-height: 1.45;
  color: #ffd9a0; background: rgba(120, 78, 0, 0.35);
  border-bottom: 1px solid rgba(255, 200, 120, 0.35);
`;

const ImageStage = styled.div`
  width: 100%; height: 100%; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  cursor: grab;
  &:active { cursor: grabbing; }
`;

export function formatSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Resolve a browser-usable URL for the attachment (ChatAttachment's cascade, condensed). */
export function useAttachmentUrl(file?: File | null, serialized?: SerializedAttachment | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    (async () => {
      if (file) {
        revoke = URL.createObjectURL(file);
        if (!cancelled) setUrl(revoke);
        return;
      }
      if (serialized?.dataUrl) { setUrl(serialized.dataUrl); return; }
      if (serialized?.storageUrl) { setUrl(serialized.storageUrl); return; }
      if (serialized?.storagePath) {
        try {
          const { getStorage, ref, getDownloadURL } = await import('firebase/storage');
          const fresh = await getDownloadURL(ref(getStorage(), serialized.storagePath));
          if (!cancelled) setUrl(fresh);
        } catch {
          if (!cancelled) setFailed(true);
        }
        return;
      }
      setFailed(true);
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [file, serialized]);
  return { url, failed };
}

/** Doc-styled wrapper for converted office content, rendered in a scriptless iframe. */
function wrapConvertedHtml(bodyHtml: string): string {
  return [
    '<!doctype html><html><head><meta charset="utf-8"><style>',
    'body{font-family:Georgia,"Times New Roman",serif;color:#1a2333;background:#fff;',
    'line-height:1.55;max-width:840px;margin:0 auto;padding:32px 24px;font-size:15px}',
    'img{max-width:100%;height:auto}',
    'h1,h2,h3,h4{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;line-height:1.25}',
    'table{border-collapse:collapse;margin:12px 0;max-width:100%}',
    'td,th{border:1px solid #cbd5e1;padding:4px 8px;font-size:13px;font-family:-apple-system,sans-serif}',
    'th{background:#f1f5f9}',
    'h2.sheet-name{margin-top:28px;border-bottom:2px solid #e2e8f0;padding-bottom:4px}',
    '</style></head><body>',
    bodyHtml,
    '</body></html>',
  ].join('');
}

export type ContentMode = 'text' | 'blob' | 'docx' | 'sheet' | 'off';

/**
 * Load the attachment BYTES for renderers that need content rather than a URL, then
 * shape them per mode: 'text' decodes UTF-8, 'blob' mints an object URL (PDF viewer),
 * 'docx' converts via mammoth, 'sheet' converts via SheetJS — both into sandbox-safe
 * HTML. Direct fetch first, server resolver fallback (the CORS fix), degraded only
 * when both fail.
 */
export function useFetchedContent(
  url: string | null,
  mode: ContentMode,
  serialized?: SerializedAttachment | null,
  declaredType?: string,
  context?: AttachmentResolveContext | null,
  retryNonce?: number,
) {
  const [text, setText] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [convertedHtml, setConvertedHtml] = useState<string | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (mode === 'off') return undefined;
    let cancelled = false;
    let revoke: string | null = null;
    setError(false);
    (async () => {
      let bytes: ArrayBuffer | null = null;
      let contentType = declaredType || '';
      if (url) {
        try {
          const resp = await fetch(url);
          if (!resp.ok) throw new Error(String(resp.status));
          bytes = await resp.arrayBuffer();
          contentType = resp.headers.get('content-type') || contentType;
        } catch {
          bytes = null;
        }
      }
      if (!bytes && canResolveServerSide(serialized, context)) {
        try {
          const resolved = await resolveAttachmentContent(serialized!, context);
          bytes = resolved.bytes;
          contentType = resolved.contentType || contentType;
        } catch {
          bytes = null;
        }
      }
      if (cancelled) return;
      if (!bytes) { setError(true); return; }
      try {
        if (mode === 'text') {
          setText(new TextDecoder('utf-8').decode(bytes));
        } else if (mode === 'blob') {
          const b = new Blob([bytes], { type: contentType || 'application/octet-stream' });
          revoke = URL.createObjectURL(b);
          setBlobUrl(revoke);
        } else if (mode === 'docx') {
          const mammoth = await import('mammoth');
          const result = await mammoth.convertToHtml({ arrayBuffer: bytes });
          if (!cancelled) setConvertedHtml(wrapConvertedHtml(result.value || ''));
        } else if (mode === 'sheet') {
          const XLSX = await import('xlsx');
          const wb = XLSX.read(bytes, { type: 'array' });
          const parts = wb.SheetNames.map((sheetName) => {
            const table = XLSX.utils.sheet_to_html(wb.Sheets[sheetName], { header: '', footer: '' });
            return `<h2 class="sheet-name">${sheetName.replace(/</g, '&lt;')}</h2>${table}`;
          });
          if (!cancelled) setConvertedHtml(wrapConvertedHtml(parts.join('')));
        }
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
    // Primitive deps only: a fresh `serialized` object identity with identical
    // locators must NOT restart the load (render-loop refetch guard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, mode, declaredType, serialized?.storagePath, serialized?.storageUrl, serialized?.dataUrl,
    serialized?.gcsPath, context?.messageId, context?.sessionId, context?.turnId, retryNonce]);
  return { text, blobUrl, convertedHtml, error };
}

/**
 * Vector images (SVG) usually declare no intrinsic pixel size — inside a
 * `max-width/max-height` box an <img> then collapses to nothing (the
 * 2026-09-01 "butterfly.svg" report: a blank preview, no error, because the
 * file loaded fine). Size them explicitly to the stage; rasters keep their
 * natural dimensions so small images are never blown up.
 */
export function isVectorImage(name: string | undefined, mimeType: string | undefined): boolean {
  const t = String(mimeType || '').toLowerCase().split(';')[0].trim();
  return t === 'image/svg+xml' || /\.svg$/i.test(String(name || ''));
}

export const ImageRenderer: React.FC<{ src: string; alt: string; vector?: boolean }> = ({ src, alt, vector = false }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => Math.min(8, Math.max(0.2, z * (e.deltaY < 0 ? 1.12 : 0.9))));
  }, []);
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ImageStage
        onWheel={onWheel}
        onMouseDown={(e) => { drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; }}
        onMouseMove={(e) => {
          if (drag.current) setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
        }}
        onMouseUp={() => { drag.current = null; }}
        onMouseLeave={() => { drag.current = null; }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{
            maxWidth: '100%', maxHeight: '100%',
            ...(vector ? { width: '100%', height: '100%', objectFit: 'contain' as const } : {}),
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
            transition: drag.current ? 'none' : 'transform 120ms ease',
          }}
        />
      </ImageStage>
      <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8 }}>
        <ToolButton onClick={() => setZoom((z) => Math.min(8, z * 1.25))} aria-label="zoom in">＋</ToolButton>
        <ToolButton onClick={() => setZoom((z) => Math.max(0.2, z * 0.8))} aria-label="zoom out">－</ToolButton>
        <ToolButton onClick={() => setRotation((r) => (r + 90) % 360)} aria-label="rotate">⟳ 90°</ToolButton>
        <ToolButton onClick={() => { setZoom(1); setRotation(0); setPan({ x: 0, y: 0 }); }} aria-label="reset view">Reset</ToolButton>
      </div>
    </div>
  );
};

export interface PreviewState {
  name: string;
  type: string;
  size: number;
  kind: PreviewKind;
  url: string | null;
  text: string | null;
  blobUrl: string | null;
  convertedHtml: string | null;
  degraded: boolean;
  download: () => void;
  /** Re-run the content ladder — a degraded preview must never be a dead end. */
  retry: () => void;
}

export function contentModeForKind(kind: PreviewKind): ContentMode {
  if (kind === 'scene3d' || kind === 'html' || kind === 'text') return 'text';
  if (kind === 'pdf') return 'blob';
  if (kind === 'docx') return 'docx';
  if (kind === 'sheet') return 'sheet';
  return 'off';
}

/** One hook powering every preview shell. */
export function usePreview(
  file?: File | null,
  serialized?: SerializedAttachment | null,
  context?: AttachmentResolveContext | null,
): PreviewState {
  const name = file?.name || serialized?.name || 'attachment';
  const type = file?.type || serialized?.type || '';
  const size = file?.size || serialized?.size || 0;
  const kind = useMemo(() => resolvePreviewKind(name, type), [name, type]);
  const { url, failed: urlFailed } = useAttachmentUrl(file, serialized);
  const contentMode = contentModeForKind(kind);
  // 2026-08-09 (user-upload preview report): a degraded preview is retryable —
  // uploads finish, tokens refresh, and the resolver's context lookup can
  // succeed on a second pass. A dead end with no retry was the old behavior.
  const [retryNonce, setRetryNonce] = useState(0);
  const { text, blobUrl, convertedHtml, error: fetchError } = useFetchedContent(
    url, contentMode, serialized, type, context, retryNonce,
  );
  // R17 (2026-08-14 "I can't even download it"): the old handler set
  // a.download on the CROSS-ORIGIN signed URL — browsers ignore the download
  // attribute cross-origin, so renderable types (HTML!) NAVIGATED to the raw
  // file instead of saving. Bytes-first now: already-fetched content becomes
  // a Blob directly; URL-only kinds ride the shared fetch→resolver ladder.
  const download = useCallback(() => {
    void (async () => {
      if (text != null) {
        saveBlobAs(new Blob([text], { type: type || 'text/plain' }), name);
        return;
      }
      if (blobUrl) {
        // Object URLs are same-origin — the download attribute is honored.
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = name;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      if (!url && !canResolveServerSide(serialized, context)) return;
      await downloadAttachmentAsFile({ url, fileName: name, declaredType: type, serialized, context });
    })();
  }, [url, blobUrl, text, type, name, serialized, context]);
  // Byte-backed kinds only degrade when BOTH the direct fetch and the server
  // resolver failed; URL-only kinds degrade when no URL could be produced.
  const degraded = contentMode === 'off'
    ? urlFailed
    : fetchError && (urlFailed || !url ? !blobUrl && text == null && convertedHtml == null : true);
  const retry = useCallback(() => setRetryNonce((n) => n + 1), []);
  return {
    name, type, size, kind, url, text, blobUrl, convertedHtml,
    degraded,
    download,
    retry,
  };
}

/**
 * Can this browser render a PDF INSIDE the page (in an <iframe>)?
 *
 * Two signals, because the standard bit answers the wrong question on iOS
 * (review 2026-08-14):
 * - navigator.pdfViewerEnabled (Chrome 94+/Firefox/Safari 16.4+) measures
 *   top-level-NAVIGATION display. Android browsers report false — there an
 *   iframe paints the browser's own dark placeholder (glyph + blob-UUID +
 *   external "Open"; owner screenshot 2026-08-14).
 * - iOS/iPadOS (all-WebKit) reports TRUE, but an iframe PDF renders
 *   first-page-only and unscrollable — a long-standing WebKit limitation
 *   the bit doesn't capture — so Apple touch platforms are card-lane
 *   regardless. (iPadOS masquerades as MacIntel; maxTouchPoints tells the
 *   truth. Touchless Macs keep the inline viewer.)
 *
 * Unknown capability (legacy desktop) → assume inline support, the
 * long-standing behavior.
 */
function inlinePdfSupported(): boolean {
  if (typeof navigator === 'undefined') return true;
  const n = navigator as Navigator & { pdfViewerEnabled?: boolean };
  const appleTouchPlatform = /iPad|iPhone|iPod/.test(n.userAgent || '')
    || (n.platform === 'MacIntel' && (n.maxTouchPoints ?? 0) > 1);
  if (appleTouchPlatform) return false;
  return typeof n.pdfViewerEnabled === 'boolean' ? n.pdfViewerEnabled : true;
}

/**
 * An href window.open can actually use: top-frame navigation to data: URLs
 * is blocked by Chrome/Firefox (silent no-op), and dataUrl-backed PDFs put
 * a data: URL FIRST in the url cascade — prefer the blob URL there (review
 * 2026-08-14). Null while neither usable form exists yet.
 */
export function openableHref(p: PreviewState): string | null {
  if (p.url && !p.url.startsWith('data:')) return p.url;
  return p.blobUrl || null;
}

/** The kind-dispatched body — identical in the modal and the floating window. */
export const PreviewBody: React.FC<{ p: PreviewState; wrap: boolean }> = ({ p, wrap }) => {
  if (p.degraded || p.kind === 'binary') {
    return (
      <CenterCard>
        <div style={{ fontSize: 40 }}>📄</div>
        <div style={{ wordBreak: 'break-word' }}><strong>{p.name}</strong></div>
        <div>{previewKindLabel(p.kind)}{p.size ? ` · ${formatSize(p.size)}` : ''}</div>
        <div style={{ fontSize: 12, color: '#8896ab' }}>
          {p.degraded && p.kind !== 'binary'
            ? 'The preview didn’t load — the file may still be uploading. Try again in a moment, or download it directly.'
            : 'This file type opens in its own application.'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {p.degraded && p.kind !== 'binary' && <ToolButton onClick={p.retry}>Try again</ToolButton>}
          {(p.url || p.blobUrl) && <ToolButton onClick={p.download}>Download</ToolButton>}
        </div>
      </CenterCard>
    );
  }
  switch (p.kind) {
    case 'image':
      return p.url ? <ImageRenderer src={p.url} alt={p.name} vector={isVectorImage(p.name, p.type)} /> : null;
    case 'video':
      return p.url ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video src={p.url} controls style={{ maxWidth: '100%', maxHeight: '100%', margin: 'auto' }} />
      ) : null;
    case 'audio':
      return p.url ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio src={p.url} controls style={{ width: 'min(560px, 90%)', margin: 'auto' }} />
      ) : null;
    case 'pdf': {
      if (!inlinePdfSupported()) {
        // The app's own card — real filename, real actions — instead of the
        // browser's broken iframe rendering. Checked BEFORE the blobUrl
        // gate: the card doesn't need the PDF's bytes, so it must not wait
        // for (or stall on) the full download the blob lane performs
        // (review 2026-08-14) — it renders as soon as ANY href exists.
        if (!p.url && !p.blobUrl) return <CenterCard>Loading PDF…</CenterCard>;
        const openHref = openableHref(p);
        return (
          <CenterCard>
            <div style={{ fontSize: 40 }}>📄</div>
            <div style={{ wordBreak: 'break-word' }}><strong>{p.name}</strong></div>
            <div>{previewKindLabel(p.kind)}{p.size ? ` · ${formatSize(p.size)}` : ''}</div>
            <div style={{ fontSize: 12, color: '#8896ab' }}>
              This device doesn’t display PDFs inside the page — open it in your viewer or save it.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              {openHref && (
                <ToolButton onClick={() => window.open(openHref, '_blank', 'noopener,noreferrer')}>
                  Open PDF
                </ToolButton>
              )}
              <ToolButton onClick={p.download}>Download</ToolButton>
            </div>
          </CenterCard>
        );
      }
      return p.blobUrl ? <FullFrame title={p.name} src={p.blobUrl} /> : <CenterCard>Loading PDF…</CenterCard>;
    }
    case 'docx':
      return p.convertedHtml != null
        ? <FullFrame title={p.name} sandbox="" srcDoc={p.convertedHtml} />
        : <CenterCard>Loading document…</CenterCard>;
    case 'sheet':
      return p.convertedHtml != null
        ? <FullFrame title={p.name} sandbox="" srcDoc={p.convertedHtml} />
        : <CenterCard>Loading spreadsheet…</CenterCard>;
    case 'scene3d': {
      // Guard injected (2026-08-09 black-preview report): a scene that dies
      // at boot — blocked CDN import, module resolution error, no WebGL —
      // now explains itself in plain language instead of sitting black.
      if (p.text == null) return <CenterCard>Loading 3D scene…</CenterCard>;
      const sceneFrame = <FullFrame title={p.name} sandbox="allow-scripts allow-pointer-lock" allow="pointer-lock *; fullscreen *" srcDoc={injectRuntimeGuard(p.text)} />;
      return htmlLooksIncomplete(p.text)
        ? <FrameColumn><IncompleteBanner>⚠️ This file appears to be incomplete — the generation may have been cut off before it finished. Ask for it to be regenerated.</IncompleteBanner>{sceneFrame}</FrameColumn>
        : sceneFrame;
    }
    case 'html': {
      // 2026-08-09 black-preview report: an interactive HTML app rendered
      // with scripts stripped paints its static background and nothing else
      // — a dead black frame with working buttons around it. Scripted HTML
      // now runs under the SAME guarantee the scene3d lane has always shipped
      // with: `allow-scripts` in an opaque origin (no allow-same-origin, so
      // the content can never touch the app origin, cookies, or storage),
      // plus the runtime guard so a boot failure still explains itself.
      // Script-free HTML keeps the fully inert frame.
      if (p.text == null) return <CenterCard>Loading…</CenterCard>;
      const htmlFrame = htmlNeedsScripts(p.text)
        ? <FullFrame title={p.name} sandbox="allow-scripts allow-pointer-lock" allow="pointer-lock *; fullscreen *" srcDoc={injectRuntimeGuard(p.text)} />
        : <FullFrame title={p.name} sandbox="" srcDoc={p.text} />;
      // R13 (2026-08-13 datacenter-hvac incident): a cut-off head fragment
      // opens like a page and renders black — say so above the frame.
      return htmlLooksIncomplete(p.text)
        ? <FrameColumn><IncompleteBanner>⚠️ This file appears to be incomplete — the generation may have been cut off before it finished. Ask for it to be regenerated.</IncompleteBanner>{htmlFrame}</FrameColumn>
        : htmlFrame;
    }
    case 'text':
      return p.text != null
        ? <TextView $wrap={wrap}>{p.text}</TextView>
        : <CenterCard>Loading…</CenterCard>;
    default:
      return null;
  }
};
